/*
    primary and secondary:

    Primary: The main brand color used for key elements like buttons, links, and primary actions.
    Secondary: A complementary color used to highlight less dominant elements or provide accents.
    background and surface:

    Background: The base color for the main application background.
    Surface: The color for components or cards that appear on top of the background.
    error:

    Used for error states, alerts, or validation messages.
    onPrimary, onSecondary, etc.:

    Colors that are meant to appear on top of their respective base colors.
    Example:
    If primary is a dark color (#6200ea), onPrimary will likely be light (#ffffff) to maintain contrast.
    Similarly, onBackground defines the text or content color that appears over the background.
*/

interface ThemeColors {
  brand: string
  second: string
  secondLight: string
  secondDark: string
  secondDarkest: string
  secondHighlight: string
  secondHighlightDeep: string
  textWhite: string
  secondary: string
  secondaryLight: string
  secondaryVeryLight: string
  secondaryVeryVeryLight: string
  secondaryLightMatte: string
  secondaryFaded: string
  primary: string
  primaryLight: string
  primaryDark: string
  primaryDarkest: string
  primaryHighlight: string
  backgroundLight: string
  background: string
  backgroundTwilight: string
  backgroundMediumDark: string
  backgroundDark: string
  backgroundVeryDark: string
  standardText: string
  fadedText: string
  fancyText: string
  veryFadedText: string
  veryVeryFadedText: string
  veryVeryVeryFadedText: string
  surface: string
  surfaceSecondary: string
  error: string
  suplementGreen: string
  suplementRed: string
  suplementYellow: string
}

interface ThemeShadows {
  light: string
  medium: string
  heavy: string
}

interface Theme {
  colors: ThemeColors
  shadows: ThemeShadows
}

const theme: Theme = {
  colors: {
    brand: '#D96E30',
    second: '#A9D9E9',
    secondLight: '#E0F7FF',
    secondDark: '#85AAB6',
    secondDarkest: '#5C747C',
    secondHighlight: '#55C8C2',
    secondHighlightDeep: '#21bbb3',
    textWhite: '#F9F9F9',
    secondary: '#A9D9E9',
    secondaryLight: '#f9d1fd',
    secondaryVeryLight: '#F4EBFF',
    secondaryVeryVeryLight: '#E0F7FF',
    secondaryLightMatte: '#e9e2f2',
    secondaryFaded: '5C747C',
    primary: '#D7CfAE',
    primaryLight: '#F2E7DA',
    primaryDark: '#BB9B73',
    primaryDarkest: '#8B7252',
    primaryHighlight: '#EAAC5E',
    backgroundLight: '#F9FAFB',
    background: '#f2f4f7',
    backgroundTwilight: '#f1f1f1',
    backgroundMediumDark: '#e5e5e5',
    backgroundDark: '#dcdcdc',
    backgroundVeryDark: '#a9a9a9',
    standardText: '#000000',
    fadedText: '#101828',
    fancyText: '#67748E',
    veryFadedText: '#475467',
    veryVeryFadedText: '#667085',
    veryVeryVeryFadedText: '#979ca3',
    surface: '#ffffff',
    surfaceSecondary: '#F9FAFB',
    error: '#b92e2e',
    suplementGreen: '#8DB28B',
    suplementRed: '#C15358',
    suplementYellow: '#EDC954',
  },
  shadows: {
    light: '0 2px 5px rgba(0, 0, 0, 0.05)',
    medium: '0 4px 8px rgba(0, 0, 0, 0.1)',
    heavy: '0 8px 16px rgba(0, 0, 0, 0.15)',
  },
}

export default theme

export type { Theme, ThemeColors, ThemeShadows }
